const CACHE_KEY = "classCodeValidationCache";
function buildCacheKey(state, classCode, description, carriers) {
  const sortedCarriers = carriers && carriers.length > 0 ? [...carriers].sort().join(",") : "__all__";
  return `${state.toLowerCase()}#${classCode.toLowerCase()}#${description.toLowerCase()}#${sortedCarriers}`;
}
function loadCache() {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveCache(cache) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // sessionStorage quota exceeded or unavailable — silently ignore
  }
}

export function buildValidationPayload(childrenLoc, carrierListArray) {
  const cache = loadCache();

  const resolvedChildrenLoc = JSON.parse(JSON.stringify(childrenLoc));

  const filteredChildrenLoc = {};

  for (const stateId in childrenLoc) {
    const location = childrenLoc[stateId];
    const state = location?.state?.value;
    if (!state) continue;

    const classCodesInfo = location.classCodesInfo || {};

    for (const classCodeId in classCodesInfo) {
      const classCodeDesc = classCodesInfo[classCodeId]?.classCodeDescription;
      if (!classCodeDesc?.value) continue;

      const ccDesc = classCodeDesc.value.trim();
      const separatorIdx = ccDesc.indexOf(":");
      if (separatorIdx === -1) continue;

      const classCode = ccDesc.slice(0, separatorIdx).trim();
      const description = ccDesc.slice(separatorIdx + 1).trim();

      const cacheKey = buildCacheKey(state, classCode, description, carrierListArray);

      if (cacheKey in cache) {
        const cached = cache[cacheKey];
        resolvedChildrenLoc[stateId].classCodesInfo[classCodeId].classCodeDescription.error = cached.error;
        if (cached.dbError) {
          resolvedChildrenLoc[stateId].classCodesInfo[classCodeId].classCodeDescription.dbError = true;
        }
      } else {
        if (!filteredChildrenLoc[stateId]) {
          filteredChildrenLoc[stateId] = {
            ...location,
            classCodesInfo: {},
          };
        }
        filteredChildrenLoc[stateId].classCodesInfo[classCodeId] = classCodesInfo[classCodeId];
      }
    }
  }

  const hasUncachedEntries = Object.keys(filteredChildrenLoc).length > 0;

  return { filteredChildrenLoc, resolvedChildrenLoc, hasUncachedEntries };
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN HELPER: applyAndCacheApiResponse
//
// After the API responds, merge the API result back into resolvedChildrenLoc
// and persist new results to the sessionStorage cache.
// ─────────────────────────────────────────────────────────────────────────────
export function applyAndCacheApiResponse(
  apiChildrenLoc,     // The childrenLoc returned from the API (only for entries that were sent)
  resolvedChildrenLoc, // The full childrenLoc with cache hits already applied
  carrierListArray
) {
  const cache = loadCache();

  for (const stateId in apiChildrenLoc) {
    const location = apiChildrenLoc[stateId];
    const state = location?.state?.value;
    if (!state) continue;

    const classCodesInfo = location.classCodesInfo || {};

    for (const classCodeId in classCodesInfo) {
      const classCodeDesc = classCodesInfo[classCodeId]?.classCodeDescription;
      if (!classCodeDesc?.value) continue;

      const ccDesc = classCodeDesc.value.trim();
      const separatorIdx = ccDesc.indexOf(":");
      if (separatorIdx === -1) continue;

      const classCode = ccDesc.slice(0, separatorIdx).trim();
      const description = ccDesc.slice(separatorIdx + 1).trim();

      // Merge API result into the full resolvedChildrenLoc
      if (resolvedChildrenLoc[stateId]?.classCodesInfo?.[classCodeId]) {
        resolvedChildrenLoc[stateId].classCodesInfo[classCodeId].classCodeDescription.error =
          classCodeDesc.error;
        if (classCodeDesc.dbError) {
          resolvedChildrenLoc[stateId].classCodesInfo[classCodeId].classCodeDescription.dbError = true;
        }
      }

      // Only cache successful validation results
      if (!classCodeDesc.dbError) {
        const cacheKey = buildCacheKey(state, classCode, description, carrierListArray);
        cache[cacheKey] = { error: classCodeDesc.error };
      }
    }
  }

  saveCache(cache);
  return resolvedChildrenLoc;
}
